// client/src/services/api.js
import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://api.outivity-golf.com' // URL for Netlify/production
  : 'https://localhost:3001';      // URL for local testing

  console.log("API_BASE_URL: ", API_BASE_URL);

// Create a central axios instance with credentials support
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, 
});

// This is an Axios interceptor that runs on every API response.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check for a 401 error AND that we are NOT already on the login page.
    if (error.response && error.response.status === 401 && window.location.pathname !== '/login') {
      console.log('Session expired or unauthorized. Redirecting to login.');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// --- USER ROUTES ---

// Login user
export const login = async (credentials) => {
  console.log("Posting to login endpoint with:", credentials);
  console.log("URL:", `${API_BASE_URL}/api/login`);
  try {
    const response = await api.post('/api/login', credentials);
    return response.data;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

// Fetch current user's profile data
export const fetchUserProfile = async (userId) => {
  try {
    const response = await api.get(`/api/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching user profile for user ${userId}:`, error);
    throw error; // Let the component handle the error state
  }
};

export const updateUserProfile = async (userId, userData) => {
  try {
    const response = await api.patch(`/api/users/${userId}`, userData);
    return response.data;
  } catch (error) {
    console.error(`Error updating user profile for user ${userId}:`, error);
    // Throw the detailed error from the server if it exists
    throw error.response?.data || { error: 'A server error occurred.' };
  }
};

// Check login session
export const checkLogin = async () => {
  try {
    const response = await api.get('/api/me');
    return response.data;
  } catch (error) {
    console.error('Error checking session:', error);
    return { loggedIn: false };
  }
};

// Logout user
export const logout = async () => {
  try {
    const response = await api.post('/api/logout');
    return response.data;
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};



// --- COURSE Routes ---

// Fetch all courses
export const fetchCourses = async () => {
  try {
    const response = await api.get('/api/golfcourses');
    console.log("fetchCourses function hit");
    return response.data;
  } catch (error) {
    console.error('Error fetching courses:', error);
    throw error;
  }
};

// Fetch search results
export const fetchSearchResults = async (filters) => {
  try {
    console.log("fetchSearchResults filters ", filters);

    // Use the 'params' option in axios instead of the URL constructor.
    // This is the cleanest way to handle query parameters.
    const response = await api.get('/api/searchcourses', { params: filters });

    console.log("fetchSearchResults function successfully hit");
    console.log("response.data ", response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching search results:', error);
    throw error;
  }
};

export const fetchTeeTimesForCourse = async ({ courseId, date }) => {
  console.log("fetchTeeTimesForCourse called for courseId ", courseId);
  try {
    // ✅ THE FIX: Use the 'params' config object instead of building the URL manually.
    // This is cleaner and works with the proxy.
    const response = await api.get('/api/fetchteetimes', {
      params: { courseId, date }
    });

    return response.data;
  } catch (error) {
    console.error(`Error fetching tee times for course ${courseId}:`, error);
    throw error;
  }
};

// Pre-flight check before opening the booking confirmation modal — lets the client offer an
// "add your credentials" prompt instead of letting the booking attempt fail.
export const checkCourseCredential = async (courseId) => {
  try {
    const response = await api.get(`/api/courses/${courseId}/credential-check`);
    return response.data;
  } catch (error) {
    console.error(`Error checking credentials for course ${courseId}:`, error);
    throw error;
  }
};


// --- BOOKING routes ---

export const initiateBooking = async (bookingData) => {
  try {
    // We send all the necessary data to our new backend endpoint
    const response = await api.post('/api/book-tee-time', bookingData);
    return response.data; // Should return { success: true, ... } or { success: false, ... }
  } catch (error) {
    console.error('Error initiating booking:', error);
    // Return a structured error object from the catch block
    return error.response?.data || { success: false, error: 'A network or server error occurred.' };
  }
};

export const scheduleBooking = async (scheduleData) => {
  console.log("scheduleBooking payload from client: ", scheduleData);
  try {
    const response = await api.post('/api/bookings/schedule', scheduleData);
    return response.data;
  } catch (error) {
    console.error('Error creating scheduled booking:', error);
    throw error.response?.data || { success: false, error: 'A network or server error occurred.' };
  }
};

export const fetchUserBookings = async () => {
  try {
    const response = await api.get('/api/bookings');
    return response.data; // Expected to be { confirmed: [], pending: [] }
  } catch (error) {
    console.error('Error fetching user bookings:', error);
    throw error;
  }
};

export const addConfirmationNumber = async (bookingId, confirmationNumber) => {
  try {
    console.log(confirmationNumber + " from addConfirmationNumber function hit in api.js for bookingId " + bookingId);
    const response = await api.patch(`/api/bookings/${bookingId}`, { confirmationNumber });
    return response.data; // Should return { success: true, booking: updatedBooking }
  } catch (error) {
    console.error(`Error adding confirmation number to booking ${bookingId}:`, error);
    throw error.response?.data || { success: false, error: 'A server error occurred.' };
  }
};


// Function to cancel a booking
export const cancelBooking = async (bookingId) => {
  try {
    // Use the .delete method for the API call
    const response = await api.delete(`/api/bookings/${bookingId}`);
    return response.data; // Should return { success: true, ... }
  } catch (error) {
    console.error(`Error canceling booking ${bookingId}:`, error);
    throw error.response?.data || { success: false, error: 'A network or server error occurred.' };
  }
};

// Function to cancel a PENDING booking
export const cancelPendingBooking = async (pendingBookingId) => {
  try {
    const response = await api.delete(`/api/pending-bookings/${pendingBookingId}`);
    return response.data;
  } catch (error) {
    console.error(`Error canceling pending booking ${pendingBookingId}:`, error);
    throw error.response?.data || { success: false, error: 'A network or server error occurred.' };
  }
};

